import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateEmail = (value: string): string | undefined => {
    if (!value.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
      return 'Please enter a valid email address';
    return undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // TODO: Implement actual password reset logic here
      await new Promise(resolve => setTimeout(resolve, 1500));

      console.log('Password reset requested for:', email);
      setIsSubmitted(true);
    } catch (err) {
      setError('Failed to send reset email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-b from-white to-[#e4f4ff]/10">
        <Header />

        <main className="flex-grow flex items-center justify-center px-4 py-16">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-mail-check-line text-white text-3xl"></i>
              </div>

              <h1 className="text-3xl font-bold text-[#22577A] mb-4">
                Check Your Email
              </h1>

              <p className="text-gray-600 mb-6 leading-relaxed">
                We've sent a password reset link to <strong className="text-gray-900">{email}</strong>
              </p>

              <div className="bg-[#e4f4ff]/20 border border-[#38a3a5]/30 rounded-xl p-4 mb-6">
                <p className="text-sm text-gray-700">
                  <i className="ri-information-line mr-2"></i>
                  The link will expire in 1 hour for security reasons.
                </p>
              </div>

              <div className="space-y-4">
                <Link
                  to="/login"
                  className="block w-full py-3 px-6 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Login
                </Link>

                <button
                  onClick={() => {
                    setIsSubmitted(false);
                    setEmail('');
                  }}
                  className="block w-full py-3 px-6 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
                >
                  Send to Different Email
                </button>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-4">Didn't receive the email?</p>
                <div className="space-y-2 text-sm text-gray-600">
                  <p className="flex items-start">
                    <i className="ri-checkbox-circle-line text-[#38a3a5] mr-2 mt-0.5"></i>
                    <span>Check your spam or junk folder</span>
                  </p>
                  <p className="flex items-start">
                    <i className="ri-checkbox-circle-line text-[#38a3a5] mr-2 mt-0.5"></i>
                    <span>Make sure you entered the correct email</span>
                  </p>
                  <p className="flex items-start">
                    <i className="ri-checkbox-circle-line text-[#38a3a5] mr-2 mt-0.5"></i>
                    <span>Wait a few minutes and check again</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center mt-6">
              <p className="text-gray-600">
                Need help?{' '}
                <Link
                  to="/contact"
                  className="text-[#38a3a5] hover:text-[#22577A] font-semibold transition-colors"
                >
                  Contact Support
                </Link>
              </p>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-white to-[#e4f4ff]/10">
      <Header />

      <main className="flex-grow flex items-center justify-center px-4 py-16">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-lock-unlock-line text-white text-3xl"></i>
            </div>
            <h1 className="text-4xl font-bold text-[#22577A] mb-3">
              Forgot Password?
            </h1>
            <p className="text-gray-600 text-lg">
              No worries! Enter your email and we'll send you reset instructions.
            </p>
          </div>

          {/* Reset Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-mail-line text-gray-400 text-lg"></i>
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError('');
                    }}
                    className={`w-full pl-12 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#38a3a5] focus:border-transparent transition-all ${
                      error ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="you@company.com"
                  />
                </div>
                {error && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <i className="ri-error-warning-line mr-1"></i>
                    {error}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-3 px-6 rounded-xl font-semibold text-lg transition-all ${
                  isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#22577A] to-[#38a3a5] hover:shadow-xl hover:scale-105 text-white'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <i className="ri-mail-send-line mr-2"></i>
                    Send Reset Link
                  </span>
                )}
              </button>

              {/* Back to Login */}
              <Link
                to="/login"
                className="flex items-center justify-center text-[#38a3a5] hover:text-[#22577A] font-medium transition-colors"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Login
              </Link>
            </form>
          </div>

          {/* Security Note */}
          <div className="mt-8 bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-start">
              <i className="ri-shield-check-line text-[#38a3a5] text-xl mr-3 flex-shrink-0 mt-0.5"></i>
              <div className="text-sm text-gray-600">
                <p className="font-semibold text-gray-900 mb-1">Security Note</p>
                <p>
                  For your security, we'll only send the reset link if this email is associated
                  with an existing account. The link will expire in 1 hour.
                </p>
              </div>
            </div>
          </div>

          {/* Additional Help */}
          <div className="text-center mt-6">
            <p className="text-gray-600">
              Remember your password?{' '}
              <Link
                to="/login"
                className="text-[#38a3a5] hover:text-[#22577A] font-semibold transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
